import type { WebGPURenderer } from 'three/webgpu'
import type { Services } from '../app/services'
import type { SpaceWorld } from '../space/contracts'
import { createGpuReadout, formatGpuLine, type GpuTiming, type GpuTimingReadout } from './gpuTiming'

/** Live object counts shown by the overlay; the loop refreshes them before `updatePerf`. */
export interface PerfCounts {
  ships: number
  projectiles: number
  torpedoes: number
  particles: number
  /** Shots dropped because the projectile pool was full (since boot). */
  drops: number
}

/** M2 counts (career mode): live chunks, fragments, wrecks and parts, and space events per second. */
export interface M2PerfCounts {
  chunks: number
  fragments: number
  wrecks: number
  parts: number
  eventsPerSec: number
}

/** What the overlay reads from the space world (its pools' `live` counts and the event ring head). */
export type M2PerfSource = Pick<SpaceWorld, 'events'> & {
  rocks: Pick<SpaceWorld['rocks'], 'chunks' | 'fragments'>
  salvage: Pick<SpaceWorld['salvage'], 'wrecks' | 'parts'>
}

/** What the frame ran on (PERF PF-1, L-E1): the quality preset, the backend, the strategic core's mode and the GPU. */
export interface PerfLabels {
  preset: string
  backend: string
  /** 'inline' = the strategic core runs on the main thread (the worker fell back); 'off' = no strategic core. */
  strategic: 'worker' | 'inline' | 'off'
  /** The GPU the renderer runs on (PERF L-E1): set once at boot from `shortGpuName(gpuName(renderer))`. */
  gpu: string
}

/**
 * F3 readout (all builds): fps, frame ms, the frame's JS split (sim ticks, features, render submit, other), idle
 * time (frame interval − JS: GPU wait, vsync or another thread), ticks per frame, sim ms per tick, draw calls,
 * triangles, ship/projectile/particle counts, and the preset, backend, strategic mode and GPU (L-E1); in career mode
 * also the M2 pools and space events per second (PERF PF-1, perf plan §3.1 and §5).
 */
export interface PerfOverlay {
  el: HTMLElement
  visible: boolean
  frames: number
  /** Frame interval (rAF), summed since the last refresh. */
  frameMs: number
  /** Sim ticks (stepSim + afterTick), summed. */
  simMs: number
  ticks: number
  /** The frame callback's JS time, summed. */
  jsMs: number
  /** `host.frame` + `host.overlay`, summed. */
  featuresMs: number
  /** `ctx.post.render()` (render submit), summed. */
  renderMs: number
  since: number
  readonly counts: PerfCounts
  /** Written by the loop while F3 is visible (constant strings: no allocation). */
  readonly labels: PerfLabels
  /** The career's space world (null or absent in sandbox mode). */
  m2?: M2PerfSource | null
  /** Space-event ring head at the last refresh. */
  m2Head?: number
  /** PERF G1: GPU time per pass class (timestamp queries while F3 is open); absent = no GPU line. */
  gpu?: GpuTiming | null
}

export function createPerfLabels(): PerfLabels {
  return { preset: '-', backend: '-', strategic: 'off', gpu: '-' }
}

export function createPerfCounts(): PerfCounts {
  return { ships: 0, projectiles: 0, torpedoes: 0, particles: 0, drops: 0 }
}

export function createM2PerfCounts(): M2PerfCounts {
  return { chunks: 0, fragments: 0, wrecks: 0, parts: 0, eventsPerSec: 0 }
}

/** `services` (after the features' init): the M2 counts show when the `m2` service exists (career mode). */
export function createPerfOverlay(services?: Services): PerfOverlay {
  const el = document.createElement('div')
  el.id = 'perf'
  el.hidden = true
  document.body.append(el)
  const m2 = services?.maybe('m2')?.space ?? null
  return {
    el,
    visible: false,
    frames: 0,
    frameMs: 0,
    simMs: 0,
    ticks: 0,
    jsMs: 0,
    featuresMs: 0,
    renderMs: 0,
    since: performance.now(),
    counts: createPerfCounts(),
    labels: createPerfLabels(),
    m2,
    m2Head: m2 ? m2.events.head : 0,
  }
}

export function togglePerf(p: PerfOverlay): void {
  p.visible = !p.visible
  p.el.hidden = !p.visible
}

/** Adds one frame: its interval, its sim ticks and its JS split (allocation-free, D-014). */
export function recordFrame(
  p: PerfOverlay,
  frameMs: number,
  simMs: number,
  ticks: number,
  jsMs: number,
  featuresMs: number,
  renderMs: number,
): void {
  p.frames++
  p.frameMs += frameMs
  p.simMs += simMs
  p.ticks += ticks
  p.jsMs += jsMs
  p.featuresMs += featuresMs
  p.renderMs += renderMs
}

/** Averages accumulated since the last refresh (per frame unless named otherwise). */
export interface PerfStats {
  fps: number
  /** Frame interval. */
  frameMs: number
  simMsPerTick: number
  /** The frame callback's JS time. */
  jsMs: number
  /** Sim ticks (stepSim + afterTick) per frame. */
  simMs: number
  /** `host.frame` + `host.overlay`. */
  featuresMs: number
  /** `ctx.post.render()` (render submit). */
  renderMs: number
  /** JS outside the three timed parts: input, beforeTicks, camera, ship views, dust, lib/env update, F3 itself. */
  otherMs: number
  /** Frame interval − JS (≥ 0): GPU wait, vsync or another thread. */
  idleMs: number
  ticksPerFrame: number
}

export function createPerfStats(): PerfStats {
  return {
    fps: 0,
    frameMs: 0,
    simMsPerTick: 0,
    jsMs: 0,
    simMs: 0,
    featuresMs: 0,
    renderMs: 0,
    otherMs: 0,
    idleMs: 0,
    ticksPerFrame: 0,
  }
}

/** Averages the window since the last refresh (`elapsedMs` long) into `out` (pure, allocation-free). */
export function readPerfStats(p: PerfOverlay, elapsedMs: number, out: PerfStats): PerfStats {
  const n = Math.max(p.frames, 1)
  out.fps = elapsedMs > 0 ? (p.frames * 1000) / elapsedMs : 0
  out.frameMs = p.frameMs / n
  out.simMsPerTick = p.simMs / Math.max(p.ticks, 1)
  out.jsMs = p.jsMs / n
  out.simMs = p.simMs / n
  out.featuresMs = p.featuresMs / n
  out.renderMs = p.renderMs / n
  out.otherMs = Math.max(0, out.jsMs - out.simMs - out.featuresMs - out.renderMs)
  out.idleMs = Math.max(0, out.frameMs - out.jsMs)
  out.ticksPerFrame = p.ticks / n
  return out
}

/** The overlay text (pure, for tests). */
export function formatPerf(
  s: PerfStats,
  drawCalls: number,
  triangles: number,
  c: PerfCounts,
  labels: PerfLabels,
  m2: M2PerfCounts | null = null,
  gpu: GpuTimingReadout | null = null,
): string {
  const base =
    `${s.fps.toFixed(0)} fps  ${s.frameMs.toFixed(2)} ms/frame\n` +
    `js ${s.jsMs.toFixed(2)} ms/frame (sim ${s.simMs.toFixed(2)} · features ${s.featuresMs.toFixed(2)} · ` +
    `render ${s.renderMs.toFixed(2)} · other ${s.otherMs.toFixed(2)})\n` +
    `idle ${s.idleMs.toFixed(2)} ms/frame\n` +
    (gpu ? `${formatGpuLine(gpu)}\n` : '') +
    `ticks ${s.ticksPerFrame.toFixed(1)}/frame\n` +
    `sim ${s.simMsPerTick.toFixed(3)} ms/tick\n` +
    `draws ${drawCalls}  tris ${triangles}\n` +
    `ships ${c.ships}  rounds ${c.projectiles}  torps ${c.torpedoes}\n` +
    `particles ${c.particles}${c.drops > 0 ? `  dropped ${c.drops}` : ''}\n` +
    `preset ${labels.preset} · backend ${labels.backend} · strategic ${labels.strategic} · gpu ${labels.gpu}`
  if (!m2) return base
  return `${base}
chunks ${m2.chunks}  frags ${m2.fragments}  wrecks ${m2.wrecks}  parts ${m2.parts}
m2 events ${m2.eventsPerSec.toFixed(1)}/s`
}

/** Reads the M2 counts; events per second over `elapsedMs` since `lastHead`. */
export function readM2Counts(src: M2PerfSource, lastHead: number, elapsedMs: number, out: M2PerfCounts): M2PerfCounts {
  out.chunks = src.rocks.chunks.live
  out.fragments = src.rocks.fragments.live
  out.wrecks = src.salvage.wrecks.live
  out.parts = src.salvage.parts.live
  out.eventsPerSec = elapsedMs > 0 ? ((src.events.head - lastHead) * 1000) / elapsedMs : 0
  return out
}

const stats = createPerfStats()
const m2Counts = createM2PerfCounts()
const gpuReadout = createGpuReadout()

/**
 * Refreshes the text at most 4 times a second. Allocation-free between refreshes; the refresh builds the text only
 * while F3 is visible.
 */
export function updatePerf(p: PerfOverlay, renderer: Pick<WebGPURenderer, 'info'>, now: number): void {
  const elapsed = now - p.since
  if (elapsed < 250) return
  if (p.visible) {
    readPerfStats(p, elapsed, stats)
    const r = renderer.info.render
    const m2 = p.m2 ? readM2Counts(p.m2, p.m2Head ?? 0, elapsed, m2Counts) : null
    const gpu = p.gpu ? p.gpu.read(gpuReadout) : null
    p.el.textContent = formatPerf(stats, r.drawCalls, r.triangles, p.counts, p.labels, m2, gpu)
  }
  if (p.m2) p.m2Head = p.m2.events.head
  p.frames = 0
  p.frameMs = 0
  p.simMs = 0
  p.ticks = 0
  p.jsMs = 0
  p.featuresMs = 0
  p.renderMs = 0
  p.since = now
}
